// src/pages/ContactsPage.tsx
//
// ★ TASK 13 — Annex A stories 11 and 12: "find the point of contact for a
// payment agency" / "find the point of contact for an HSP" — the SAME
// directory read from opposite directions, `GET /api/contacts` (Task 12).
//
// ⚠ Deliberately CROSS-PARTY: an HSP-scoped session reads a Payment Agency's
// own contact here, and vice versa — see
// `services/transfer/src/counterparty-contact-repository.ts`'s own header
// for the authorisation rule (any real grant reads the whole directory; a
// scope holding none reads nothing) and **A-33**/**Q-33** for what is
// registered rather than assumed.
//
// ⚠ Export: printable via the browser's own print function, same as
// RecordsPage — PDF/Excel download is NOT built (Phase 5 Task 14).
import { plainCode } from '../lib/plain-words'
import { useEffect, useState } from 'react'
import { SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { CategoryBadge } from '../components/StatusBadge'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { formatInstant } from '../lib/format'
import { targetValue } from '../lib/sgds-dom'
import { CONTACT_ROLES, type ContactRoleValue } from '../lib/types'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

const ALL = 'ALL'

/** Mirrors `CONTACT_SORT_COLUMNS` in `services/transfer/src/counterparty-contact-repository.ts`. */
type ContactSortKey = 'counterpartyCode' | 'contactName' | 'contactRole' | 'updatedAt' | 'counterpartyKind' | 'phone' | 'email' | 'availabilityHours'

const COLUMN_LABELS: Readonly<Record<ContactSortKey, string>> = {
  counterpartyCode: 'Agency',
  contactName: 'Name',
  counterpartyKind: 'Kind',
  phone: 'Phone',
  email: 'Email',
  availabilityHours: 'Availability',
  contactRole: 'Contact type',
  updatedAt: 'Last updated',
}

const CONTACT_ROLE_LABELS: Readonly<Record<ContactRoleValue, string>> = {
  BILLING: 'Billing',
  TECHNICAL: 'Technical',
  MANAGEMENT: 'Management',
}

/**
 * Rows per page. Comfortably under this surface's own `MAX_*_LIMIT` at the
 * gateway, so the server never has to clamp what the screen asked for — a
 * clamped request would silently show fewer rows than the pager's arithmetic
 * assumes and put the page count out by one.
 */
const PAGE_SIZE = 25

export function ContactsPage() {
  const { sort, onSort, interacted } = useSort<ContactSortKey>()
  const [roleFilter, setRoleFilter] = useState<string>(ALL)
  const [kindFilter, setKindFilter] = useState<string>(ALL)

  /**
   * ★★ THE PAGE OFFSET — 2026-08-18. This screen showed the first page of a
   * population it also told the officer the size of, and no control could reach
   * the rest; `services/gateway/src/list-reachability.test.ts` is the census
   * that found it. Held here rather than inside `useOffsetPager` because the
   * FETCH is keyed on it, and the hook is deliberately unaware of fetching.
   */
  const [pagerOffset, setPagerOffset] = useState(0)

  const contacts = useApi(
    () =>
      api.contacts({
        ...sortParams(sort),
        ...(roleFilter !== ALL && { contactRole: roleFilter as ContactRoleValue }),
        ...(kindFilter !== ALL && { kind: kindFilter as 'HSP' | 'PAYMENT' | 'RECOVERY' | 'ENTITLEMENT' }),
        offset: pagerOffset,
      }),
    [sort.column, sort.dir, roleFilter, kindFilter, pagerOffset],
  )

  /**
   * ★★ WAVE 9 — one statement of what the download is asked for, shared by both
   * format buttons and built from the same state the table's own fetch uses.
   *
   * ⚠ Without the OFFSET, deliberately: the route accepts none, because an export
   * is one document over one filter rather than the page of it in front of the
   * officer.
   */
  const exportState = {
    ...sortParams(sort),
    ...(roleFilter !== ALL && { contactRole: roleFilter }),
    ...(kindFilter !== ALL && { kind: kindFilter }),
  }

  const rows = contacts.data?.contacts ?? []
  const total = contacts.data?.total ?? 0
  const pager = useOffsetPager(PAGE_SIZE, total)

  // ⚠ The pager owns the page NUMBER; this component owns the offset it FETCHES
  // with. Kept in step in one place rather than becoming two sources of one
  // fact — S-011's shape, in a component.
  useEffect(() => {
    if (pager.offset !== pagerOffset) setPagerOffset(pager.offset)
  }, [pager.offset, pagerOffset])

  // ⚠ A FILTER or SORT change is a different population. Page three of one is
  // not page three of the other, and leaving the offset alone shows an empty
  // table that reads as "no results" when there are plenty.
  useEffect(() => {
    pager.reset()
  }, [sort.column, sort.dir, roleFilter, kindFilter])

  // ⚠ No cursor-based pager: the directory is small reference data (a
  // handful of contacts per counterparty), so the route returns the whole
  // scoped population up to MAX_CONTACT_LIMIT in one page — see
  // `PostgresCounterpartyContactRepository`'s own header for why that bound
  // is not a byte-measured one the way `MAX_RECORD_LIMIT` is.

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Contacts</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            The point of contact for a counterparty you exchange bills or payments with: billing, technical or
            management, by agency. Cross-party by design: this directory is not limited to your own agency's
            contact, because the whole point is finding the OTHER party's.
          </p>
        </div>
        {/*
          ★★★ WAVE 27 — one control group, owner-directed. These answer a single
          question — "what do I want to do with this list?" — and were arranged as if
          they answered three or four. See `ExportToolbar` for the whole argument.
        */}
        <ExportToolbar
          onPrint={() => window.print()}
          onDownload={(format) => {
            window.location.href = api.contactsExportUrl(format, exportState)
          }}
          email={<EmailReportButton path="/api/contacts/export" />}
        />
      </div>

      <div className="pt-card pt-controlbar pt-no-print">
        <SgdsSelect label="Contact type" value={roleFilter} onSgdsChange={(e) => setRoleFilter(targetValue(e))}>
          <SgdsSelectOption value={ALL}>All contact types</SgdsSelectOption>
          {CONTACT_ROLES.map((role) => (
            <SgdsSelectOption key={role} value={role}>
              {CONTACT_ROLE_LABELS[role]}
            </SgdsSelectOption>
          ))}
        </SgdsSelect>
        <SgdsSelect label="Agency kind" value={kindFilter} onSgdsChange={(e) => setKindFilter(targetValue(e))}>
          <SgdsSelectOption value={ALL}>All kinds</SgdsSelectOption>
          <SgdsSelectOption value="HSP">Healthcare Service Provider</SgdsSelectOption>
          <SgdsSelectOption value="PAYMENT">Payment Agency</SgdsSelectOption>
          <SgdsSelectOption value="RECOVERY">Recovery Agency</SgdsSelectOption>
          <SgdsSelectOption value="ENTITLEMENT">Entitlement Agency</SgdsSelectOption>
        </SgdsSelect>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={contacts.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {contacts.error !== null ? (
        <ErrorCard title="Could not load contacts" message={contacts.error} onRetry={contacts.reload} />
      ) : contacts.initialLoading ? (
        <LoadingCard label="Loading contacts" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No contacts in scope"
          message="Either nothing matches this filter, or your active identity holds no grant at all: the directory refuses a scope with no grants, the same way the rest of this system does."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={contacts.loading}>
            <table className="pt-table">
              <caption className="sr-only">
                Agency contacts in your scope, narrowed by the contact-type and agency-kind filters above
              </caption>
              <thead>
                <tr>
                  <SortableTh label="Agency" sortKey="counterpartyCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Kind" sortKey="counterpartyKind" sort={sort} onSort={onSort} />
                  <SortableTh label="Name" sortKey="contactName" sort={sort} onSort={onSort} />
                  <SortableTh label="Contact type" sortKey="contactRole" sort={sort} onSort={onSort} />
                  <SortableTh label="Phone" sortKey="phone" sort={sort} onSort={onSort} />
                  <SortableTh label="Email" sortKey="email" sort={sort} onSort={onSort} />
                  <SortableTh label="Availability" sortKey="availabilityHours" sort={sort} onSort={onSort} />
                  <SortableTh label="Last updated" sortKey="updatedAt" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((contact) => (
                  <tr key={contact.id}>
                    <td className="font-semibold">{contact.counterpartyCode}</td>
                    <td><CategoryBadge kind="contactKind" value={contact.counterpartyKind} label={plainCode(contact.counterpartyKind)} /></td>
                    <td>{contact.contactName}</td>
                    <td>
                      <CategoryBadge kind="code" value={contact.contactRole} label={CONTACT_ROLE_LABELS[contact.contactRole]} />
                    </td>
                    <td className="pt-mono text-sm">{contact.phone}</td>
                    <td className="text-sm">{contact.email}</td>
                    <td className="text-sm">{contact.availabilityHours ?? 'Not recorded'}</td>
                    <td className="text-sm" title={formatInstant(contact.updatedAt)}>
                      {formatInstant(contact.updatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} contact{total === 1 ? '' : 's'} in your scope.{' '}
            {sort.column === null
              ? 'The server default order.'
              : `Sorted on the server by ${COLUMN_LABELS[sort.column]} (${sort.dir === 'asc' ? 'ascending' : 'descending'}).`}
          </p>
          <Pagination pager={pager} label="contacts" total={total} shown={rows.length} />
        </div>
      )}
    </div>
  )
}
